import { Color, Spacing } from '@persona-id/design-tokens';
import { isEqual, kebabCase, pullAllBy, unionBy } from 'lodash/fp';
import { DateTime, Duration } from 'luxon';
import { memo, ReactNode, useState } from 'react';
import styled from 'styled-components';

import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Account } from 'dashboard/models/account';
import { Case } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { AttributeColumn } from 'dashboard/models/table-view';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import { BulkActionCaseInfo } from 'dashboard/views/DashboardCasesView/DashboardCasesView';
import Checkbox from 'design-system/components/Checkbox';
import Sticker from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';

const SORTABLE_COLUMN_KEYS = ['created_at', 'resolved_at', 'status', 'active_sla_expires_at'];

const Placeholder = styled('div')`
  color: ${Color.CoreSlate700};
`;

const Warning = styled('div')`
  color: ${({ theme }) => theme.color.variants.danger.default};
`;

const StatusStickerCell = styled('div')`
  max-width: 400px;
`;

const StickerGroup = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing[1]};
  max-width: 400px;
`;

const SLA_MIN_DAYS = 3;
const SLA_MIN_HOURS = 1;
const SLA_MIN_MINUTES = 1;

export const formatSlaTime = (slaExpiresAt: string | undefined) => {
  if (!slaExpiresAt) {
    return <Placeholder>None</Placeholder>;
  }

  const timeToSlaExpiration = DateTime.fromISO(slaExpiresAt).diff(
    DateTime.local(),
    'seconds',
  ).seconds;

  const duration = Duration.fromObject({ seconds: Math.abs(timeToSlaExpiration) });
  const days = duration.as('days');
  const hours = duration.as('hours');
  if (days >= SLA_MIN_DAYS) {
    return timeToSlaExpiration < 0
      ? DateTime.fromISO(slaExpiresAt)
          .toRelative()
          ?.replace(/( ago)/, '')
      : // Remove the "in" prefix from the relative time when it's in the future
        DateTime.fromISO(slaExpiresAt)
          .toRelative()
          ?.replace(/^(in )/, '');
  } else {
    const minutes = duration.as('minutes');
    if (hours >= SLA_MIN_HOURS) {
      return `${Math.floor(hours)} ${hours >= 2 ? 'hours' : 'hour'}`;
    }

    if (minutes >= SLA_MIN_MINUTES) {
      return `${Math.floor(minutes)} ${minutes >= 2 ? 'minutes' : 'minute'}`;
    }

    return 'a few seconds';
  }
};

export const columnDataMapping = (
  kase: Case,
  columnKey: string,
  accounts: Account[],
  caseCaseQueues: CaseQueue[],
  caseUsers: User[],
  tags: Tag[],
): ReactNode => {
  switch (columnKey) {
    case 'id':
      return kase.attributes.name;
    case 'created_at': {
      return kase.attributes['created-at'] ? (
        DateTime.fromISO(kase.attributes['created-at']).toFormat(DATE_AND_TIME_FORMAT)
      ) : (
        <Placeholder>None</Placeholder>
      );
    }
    case 'resolved_at': {
      return kase.attributes['resolved-at'] ? (
        DateTime.fromISO(kase.attributes['resolved-at']).toFormat(DATE_AND_TIME_FORMAT)
      ) : (
        <Placeholder>None</Placeholder>
      );
    }
    case 'status': {
      return (
        <StatusStickerCell>
          <CaseStatusSticker caseState={kase.attributes.state} />
        </StatusStickerCell>
      );
    }
    case 'active_sla_expires_at': {
      if (!kase.attributes['sla-expires-at'] || kase.attributes['resolved-at']) {
        return <Placeholder>None</Placeholder>;
      }

      const timeToSlaExpiration = DateTime.fromISO(kase.attributes['sla-expires-at']).diff(
        DateTime.local(),
        'seconds',
      ).seconds;
      const slaTimeFormatted = kase.attributes['sla-expires-at']
        ? formatSlaTime(kase.attributes['sla-expires-at'])
        : null;

      if (timeToSlaExpiration < 0) {
        return <Warning>{slaTimeFormatted} overdue</Warning>;
      } else {
        return `${slaTimeFormatted} remaining`;
      }
    }
    case 'case_queue_name': {
      const caseQueue =
        kase.relationships['case-queue'].data != null
          ? (caseCaseQueues.find(
              (ccq) => ccq.id === kase.relationships['case-queue'].data.id,
            ) as CaseQueue)
          : undefined;
      return caseQueue ? caseQueue.attributes.name : <Placeholder>None</Placeholder>;
    }
    case 'tags': {
      // NB: in the event that an org has no tags
      if (tags.length === 0) {
        return <Placeholder>None</Placeholder>;
      }
      const tagRelationships = kase.relationships.tags.data;
      const tagModels = tagRelationships.map((relationship) =>
        tags.find((tag) => tag.id === relationship.id),
      );

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const tagNames = tagModels.map((tag) => tag.attributes.name);

      return (
        <StickerGroup>
          {tagNames.length > 0 ? (
            tagNames.map((tagName) => <Sticker key={tagName}>{tagName}</Sticker>)
          ) : (
            <Placeholder>None</Placeholder>
          )}
        </StickerGroup>
      );
    }
    case 'assignee': {
      const assignee =
        kase.relationships.assignee.data != null
          ? (caseUsers.find(
              (apiModel) => apiModel.id === kase.relationships.assignee.data.id,
            ) as User)
          : undefined;
      return assignee ? userDisplayName(assignee) : <Placeholder>Unassigned</Placeholder>;
    }
    case 'account.full_name': {
      // NB: If there are any accounts, only look at the first account for now. We don't have a model for dealing with multiple case objects yet.
      const account =
        kase.relationships.accounts.data.length > 0
          ? (accounts.find(
              (apiModel) => apiModel.id === kase.relationships.accounts.data[0].id,
            ) as Account)
          : undefined;
      return account ? (
        (account.attributes['name-full-localized'] ?? account.attributes['name-full'] ?? (
          <Placeholder>No account name</Placeholder>
        ))
      ) : (
        <Placeholder>No account</Placeholder>
      );
    }
    case 'account.reference_id': {
      // NB: If there are any accounts, only look at the first account for now. We don't have a model for dealing with multiple case objects yet.
      const account =
        kase.relationships.accounts.data.length > 0
          ? (accounts.find(
              (apiModel) => apiModel.id === kase.relationships.accounts.data[0].id,
            ) as Account)
          : undefined;
      return account ? (
        (account.attributes['reference-id'] ?? <Placeholder>No account reference ID</Placeholder>)
      ) : (
        <Placeholder>No account</Placeholder>
      );
    }
    default: {
      // NB: need to convert the snake case to kebab case when accessing the object.
      const kebabCasedKey = kebabCase(columnKey);
      return kebabCasedKey in kase.attributes.fields ? (
        String(kase.attributes.fields[kebabCasedKey].value)
      ) : (
        <Placeholder>None</Placeholder>
      );
    }
  }
};

export const columnLabelMapping = (column: AttributeColumn): string => {
  switch (column.key) {
    case 'created_at': {
      return `Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`;
    }
    case 'resolved_at': {
      return `Resolved at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`;
    }
    default: {
      return column.label;
    }
  }
};

export interface CasesTableProps {
  accounts?: Account[];
  cases: Case[];
  caseUsers: User[];
  caseCaseQueues: CaseQueue[];
  tags?: Tag[];
  sortParams: SortParams;
  hasUsedFilters: boolean;
  hasFilterOnlyOnOpenCases: boolean;
  isLoading: boolean;
  selectedCasesInfo: BulkActionCaseInfo[];
  columnOrdering: AttributeColumn[];
  maySelectCases: boolean;
  setSelectedCasesInfo: (casesInfo: BulkActionCaseInfo[]) => void;
  handleSortUpdate?: (sortParams: SortParams) => void;
}

interface CasesTableRowProps {
  case: Case;
  checked: boolean;
  columns: AttributeColumn[];
}

const CasesTable = ({
  accounts = [],
  cases,
  caseUsers,
  caseCaseQueues,
  tags = [],
  sortParams,
  hasUsedFilters,
  hasFilterOnlyOnOpenCases,
  isLoading,
  selectedCasesInfo,
  columnOrdering,
  maySelectCases,
  setSelectedCasesInfo,
  handleSortUpdate,
}: CasesTableProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [lastSelectedCaseIndex, setLastSelectedCaseIndex] = useState<number>(undefined);
  const currentCaseIds = cases.map((kase: Case) => kase.id);

  const handleColumnHeaderClick = (columnName: string) => {
    if (handleSortUpdate) {
      handleSortUpdate({
        sort: columnName,
        shouldSortDescending:
          columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
      });
    }
  };

  // Should eventually extract out this logic, see PER-6002
  const handleSelectCase = (caseIndex: number, shiftKey: boolean) => {
    const newCase = cases[caseIndex];
    const newCaseInfo: BulkActionCaseInfo = {
      caseId: newCase.id,
      status: newCase.attributes.status,
      state: newCase.attributes.state,
      caseTemplateId: newCase.relationships['case-template'].data.id,
    };
    const isUnselectMode = selectedCasesInfo.some(
      (kaseInfo: BulkActionCaseInfo) => kaseInfo.caseId === newCase.id,
    );

    let newCasesInfo: BulkActionCaseInfo[];
    if (lastSelectedCaseIndex != null && shiftKey) {
      const startIndex = Math.min(caseIndex, lastSelectedCaseIndex);
      const endIndex = Math.max(caseIndex, lastSelectedCaseIndex);

      newCasesInfo = cases.slice(startIndex, endIndex + 1).map((kase: Case) => {
        return {
          caseId: kase.id,
          status: kase.attributes.status,
          state: kase.attributes.state,
          caseTemplateId: kase.relationships['case-template'].data.id,
        };
      });

      newCasesInfo = isUnselectMode
        ? pullAllBy('caseId', newCasesInfo, selectedCasesInfo)
        : unionBy('caseId', selectedCasesInfo, newCasesInfo);
    } else {
      newCasesInfo = isUnselectMode
        ? pullAllBy('caseId', [newCaseInfo], selectedCasesInfo)
        : unionBy('caseId', selectedCasesInfo, [newCaseInfo]);
    }

    setSelectedCasesInfo(newCasesInfo);
    setLastSelectedCaseIndex(caseIndex);
  };

  const caseRows = cases.map((kase, key) => {
    const columns = columnOrdering;

    const checked = selectedCasesInfo.some(
      (kaseInfo: BulkActionCaseInfo) => kaseInfo.caseId === kase.id,
    );

    const CaseRow = ({ case: kase, checked, columns: columnOrdering }: CasesTableRowProps) => {
      const caseRowCells = columnOrdering.map((column, i) => {
        return (
          <TableTD key={i}>
            {columnDataMapping(kase, column.key, accounts, caseCaseQueues, caseUsers, tags)}
          </TableTD>
        );
      });

      return (
        <TableLinkTR
          to={`/dashboard/cases/${kase.id}`}
          data-test={`cases-table__row--${kase.id}`}
          id={`cases-table__row--${kase.id}`}
        >
          {maySelectCases && (
            <TableTD
              id={`checkbox--${kase.id}`}
              onClick={(e) => {
                e.preventDefault();
                handleSelectCase(key, e.shiftKey);
              }}
            >
              <Checkbox
                name={`cases-table__checkbox--${kase.id}`}
                data-test={`cases-table__checkbox--${kase.id}`}
                checked={checked}
                onChange={() => {
                  // TODO: Make checkbox keyboard interactable
                }}
              />
            </TableTD>
          )}
          {caseRowCells}
        </TableLinkTR>
      );
    };

    // We memoize each row to prevent rerendering the entire table when only one row's state changes.
    // Without this, checking one row will cause all rows to rerender.
    const MemoizedCaseRow = memo(CaseRow, (prevProps, nextProps) => {
      const { case: prevCase, checked: prevChecked } = prevProps;
      const { case: nextCase, checked: nextChecked } = nextProps;

      return isEqual(prevCase, nextCase) && prevChecked === nextChecked;
    });
    return <MemoizedCaseRow case={kase} checked={checked} columns={columns} key={kase.id} />;
  });

  const caseHeaders = columnOrdering.map((column, i) => {
    const dataTest = `cases-table__${column.key}-header`;
    return SORTABLE_COLUMN_KEYS.includes(column.key) && handleSortUpdate ? (
      <TableTH
        key={i}
        id={dataTest}
        data-test={dataTest}
        canSort={true}
        isDescending={sortParams.shouldSortDescending}
        isSorted={sortParams.sort === column.key}
        onClick={() => handleColumnHeaderClick(column.key)}
        isSticky
      >
        {columnLabelMapping(column)}
      </TableTH>
    ) : (
      <TableTH data-test={dataTest} id={dataTest} key={i} isSticky>
        {columnLabelMapping(column)}
      </TableTH>
    );
  });

  const handleSelectAll = () => {
    let newCases = cases.map((kase: Case) => {
      return {
        caseId: kase.id,
        status: kase.attributes.status,
        state: kase.attributes.state,
        caseTemplateId: kase.relationships['case-template'].data.id,
      };
    });

    newCases = isSelectAllChecked
      ? pullAllBy('caseId', newCases, selectedCasesInfo)
      : unionBy('caseId', selectedCasesInfo, newCases);
    setSelectedCasesInfo(newCases);
  };

  const isSelectAllChecked =
    selectedCasesInfo.length > 0 &&
    currentCaseIds.every((id: string) =>
      selectedCasesInfo.find((caseInfo: BulkActionCaseInfo) => caseInfo.caseId === id),
    );

  const isSelectAllIndeterminate =
    !isSelectAllChecked &&
    currentCaseIds.some((id: string) =>
      selectedCasesInfo.find((caseInfo: BulkActionCaseInfo) => caseInfo.caseId === id),
    );

  const nullStateText =
    columnOrdering.length === 0
      ? 'Configure the fields shown to see your cases'
      : 'You have no more cases to review!';

  return (
    <TableLayout
      dataTest='cases-table'
      isLoading={isLoading}
      showHeader={true}
      isEmpty={cases.length === 0 || columnOrdering.length === 0}
      nullState={
        <TableNullState
          dataTestBlock='cases-table'
          hasUsedFilters={hasUsedFilters && !hasFilterOnlyOnOpenCases}
        >
          {nullStateText}
        </TableNullState>
      }
      containerMaxHeight={720}
      headerTR={
        <>
          {maySelectCases && (
            <TableTH isSticky>
              <Checkbox
                checked={isSelectAllChecked}
                indeterminate={isSelectAllIndeterminate}
                onClick={handleSelectAll}
                id='cases-table__select-all'
                data-test='cases-table__checkbox-header'
                aria-label='Select all cases'
              />
            </TableTH>
          )}
          {caseHeaders}
        </>
      }
    >
      {caseRows}
    </TableLayout>
  );
};

export default CasesTable;
